import type { SearchSchemaInput } from '@tanstack/react-router';

/**
 * Shared shape for every list screen's URL state.
 *
 * List state lives in the URL, never in component state. That is what makes a
 * filtered table shareable in Slack, survivable across a refresh, and correct
 * when the user hits the back button — and it is why TanStack Router was chosen
 * over React Router: `validateSearch` makes these params typed at the route
 * boundary instead of stringly-typed everywhere downstream.
 *
 * The field names are the backend's, deliberately: `limit` not `pageSize`,
 * `search` not `q`, `sortBy`/`sortOrder` not `sort`/`order`. Every list
 * endpoint accepts exactly these (see the backend's `PaginationQueryDto`), so
 * this object can be handed to a generated hook as-is. A prettier set of URL
 * names would mean a translation layer between the URL and the API, and one
 * more place for the two vocabularies to drift apart.
 */
export interface ListSearch {
  page: number;
  limit: number;
  sortBy?: string | undefined;
  sortOrder?: 'asc' | 'desc' | undefined;
  search?: string | undefined;
}

/** Matches the backend's `DEFAULT_PAGE_SIZE`. */
export const DEFAULT_LIMIT = 20;
/** The backend caps `limit` at 100; anything larger is rejected as a 400. */
const ALLOWED_LIMITS = [10, 20, 50, 100];

/**
 * Coerces raw URL search params into `ListSearch`.
 *
 * Everything here is defensive on purpose: search params are user input, and a
 * hand-edited `?page=-4` must degrade to page 1 rather than reach the API —
 * which, with `forbidNonWhitelisted` validation on the backend, would come back
 * as a 400 rather than something the user can act on.
 *
 * The `& SearchSchemaInput` on the parameter is load-bearing. It tells TanStack
 * Router to type *navigation input* from the parameter (everything optional)
 * while typing *reads* from the return value (page and limit guaranteed).
 * Without it, `navigate({ to: '/players' })` would demand an explicit page and
 * limit at every call site.
 *
 * Extend per module by spreading this and adding module-specific filters:
 *
 *   validateSearch: (search: PlayersSearchInput & SearchSchemaInput) => ({
 *     ...validateListSearch(search),
 *     status: parseEnum(search.status, ['active', 'archived'] as const),
 *   })
 */
export function validateListSearch(
  search: Partial<Record<keyof ListSearch, unknown>> & SearchSchemaInput,
): ListSearch {
  const page = toPositiveInt(search.page, 1);
  const requestedLimit = toPositiveInt(search.limit, DEFAULT_LIMIT);
  const limit = ALLOWED_LIMITS.includes(requestedLimit) ? requestedLimit : DEFAULT_LIMIT;

  const sortBy = toTrimmedString(search.sortBy);
  const sortOrder =
    search.sortOrder === 'asc' || search.sortOrder === 'desc' ? search.sortOrder : undefined;

  return {
    page,
    limit,
    // An order without a field (or vice versa) is meaningless; drop both.
    sortBy: sortBy && sortOrder ? sortBy : undefined,
    sortOrder: sortBy && sortOrder ? sortOrder : undefined,
    search: toTrimmedString(search.search),
  };
}

/** Narrows an unknown search value to one of a fixed set of allowed values. */
export function parseEnum<const T extends readonly string[]>(
  value: unknown,
  allowed: T,
): T[number] | undefined {
  return typeof value === 'string' && allowed.includes(value) ? (value as T[number]) : undefined;
}

function toPositiveInt(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function toTrimmedString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}
